import { useState, useEffect, useContext } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { useNavigate } from 'react-router-dom';
import { AuthContext, ThemeContext, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { MapPin, Users, TrendingUp, LogOut, UserCircle, UserPlus, Trophy, Rss, Crown, Calendar, Heart, Route, Map, List, Star, Store } from 'lucide-react';
import { toast } from 'sonner';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import NotificationBell from '@/components/NotificationBell';
import LiveSpecialsFeed from '@/components/LiveSpecialsFeed';
import MatrixRain from '@/components/MatrixRain';
import LocalMatrixToggle, { useMatrixMode } from '@/components/LocalMatrixToggle';
import SocialFeed from '@/components/SocialFeed';
import EnableNotificationsButton from '@/components/EnableNotificationsButton';
import FollowVenueButton from '@/components/FollowVenueButton';
import VersionBadge from '@/components/VersionBadge';
import GamificationStats from '@/components/GamificationStats';
import StoriesBar from '@/components/StoriesBar';
import BarFeedCard from '@/components/BarFeedCard';
import HappyHourAlerts from '@/components/HappyHourAlerts';
import TonightEvents from '@/components/EventCalendar';
import BarPassport from '@/components/BarPassport';
import WeeklyChallenges from '@/components/WeeklyChallenges';
import LiveFeed from '@/components/LiveFeed';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom marker icons - using SVG for better cross-browser/mobile compatibility
const partnerIcon = new L.DivIcon({
  className: 'custom-marker partner-marker',
  html: `<svg width="40" height="48" viewBox="0 0 40 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 0C8.954 0 0 8.954 0 20c0 11.046 20 28 20 28s20-16.954 20-28C40 8.954 31.046 0 20 0z" fill="#9333EA"/>
    <path d="M20 0C8.954 0 0 8.954 0 20c0 11.046 20 28 20 28s20-16.954 20-28C40 8.954 31.046 0 20 0z" stroke="#FFFFFF" stroke-width="3"/>
    <circle cx="20" cy="18" r="8" fill="#FFFFFF"/>
  </svg>`,
  iconSize: [40, 48],
  iconAnchor: [20, 48],
  popupAnchor: [0, -48]
});

const nonPartnerIcon = new L.DivIcon({
  className: 'custom-marker non-partner-marker',
  html: `<svg width="28" height="34" viewBox="0 0 28 34" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 0C6.268 0 0 6.268 0 14c0 7.732 14 20 14 20s14-12.268 14-20C28 6.268 21.732 0 14 0z" fill="#6B7280" fill-opacity="0.8"/>
    <path d="M14 0C6.268 0 0 6.268 0 14c0 7.732 14 20 14 20s14-12.268 14-20C28 6.268 21.732 0 14 0z" stroke="#9CA3AF" stroke-width="2"/>
  </svg>`,
  iconSize: [28, 34],
  iconAnchor: [14, 34],
  popupAnchor: [0, -34]
});

const Home = () => {
  const { user, logout, refreshUser } = useContext(AuthContext);
  const { darkMode, setDarkMode, matrixSkin, setMatrixSkin } = useContext(ThemeContext);
  const navigate = useNavigate();
  const patronMode = useMatrixMode('nb-patron-mode', 'neon');
  const patronMatrix = patronMode === 'matrix';
  const [bars, setBars] = useState([]);
  const [allNearbyBars, setAllNearbyBars] = useState([]);
  const [featuredBars, setFeaturedBars] = useState([]);
  const [barStats, setBarStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [seeded, setSeeded] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [googlePlacesEnabled, setGooglePlacesEnabled] = useState(false);
  const [barFilter, setBarFilter] = useState('all'); // 'all', 'partners', 'non-partners'
  const [viewMode, setViewMode] = useState('map'); // 'map' or 'list'
  const [promotedPosts, setPromotedPosts] = useState([]);
  const [barFeed, setBarFeed] = useState([]);
  const [feedView, setFeedView] = useState('cards'); // 'cards' or 'list'
  const [feedPage, setFeedPage] = useState(1);
  const FEED_PAGE_SIZE = 6;

  // Filter bars based on selected filter
  const filteredBars = allNearbyBars.filter(bar => {
    if (barFilter === 'all') return true;
    if (barFilter === 'partners') return bar.is_partner === true;
    if (barFilter === 'non-partners') return bar.is_partner === false;
    return true;
  });

  useEffect(() => {
    // Refresh user data on mount to ensure profile picture is up to date
    if (refreshUser) refreshUser();
    
    // Get user location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.log('Geolocation error:', error);
          // Default to Old Town Temecula, CA
          setUserLocation({ lat: 33.4936, lng: -117.1484 });
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
      );
    } else {
      // Geolocation not supported, default to Old Town Temecula
      setUserLocation({ lat: 33.4936, lng: -117.1484 });
    }
  }, []);

  // Seed data and fetch bars once we have location
  useEffect(() => {
    if (userLocation) {
      seedData();
      fetchNearbyBars();
    }
  }, [userLocation]);

  const seedData = async () => {
    try {
      // Always reseed to use user's current location
      if (userLocation) {
        await axios.post(`${API}/reseed?lat=${userLocation.lat}&lng=${userLocation.lng}`);
      }
      setSeeded(true);
      fetchBars();
    } catch (e) {
      console.error(e);
      fetchBars();
    }
  };

  const fetchNearbyBars = async () => {
    if (!userLocation) {
      console.log('No user location yet');
      return;
    }
    
    console.log('Fetching nearby bars for location:', userLocation);
    
    try {
      const response = await axios.get(`${API}/nearby-bars`, {
        params: {
          lat: userLocation.lat,
          lng: userLocation.lng,
          radius: 19312
        }
      });
      console.log('Nearby bars response:', response.data);
      let barsData = response.data.bars || [];
      
      // If no bars returned, fallback to partner bars directly
      if (barsData.length === 0) {
        console.log('No nearby bars, fetching partner bars directly...');
        const partnerRes = await axios.get(`${API}/bars`);
        barsData = (partnerRes.data || []).map(bar => ({
          ...bar,
          is_partner: true
        }));
        console.log('Loaded partner bars as fallback:', barsData.length);
      }
      
      console.log('Setting allNearbyBars to:', barsData.length, 'bars');
      setAllNearbyBars(barsData);
      setGooglePlacesEnabled(barsData.length > 0);
    } catch (error) {
      console.log('Nearby bars error:', error);
      // Fallback: use partner bars directly
      try {
        const partnerRes = await axios.get(`${API}/bars`);
        const partnerBars = (partnerRes.data || []).map(bar => ({
          ...bar,
          is_partner: true
        }));
        console.log('Fallback to partner bars:', partnerBars.length);
        setAllNearbyBars(partnerBars);
        setGooglePlacesEnabled(partnerBars.length > 0);
      } catch (e) {
        console.error('Failed to load any bars');
      }
    }
  };

  const fetchBars = async () => {
    try {
      const [barsRes, featuredRes, promoRes, feedRes] = await Promise.all([
        axios.get(`${API}/bars`),
        axios.get(`${API}/bars/featured/list`),
        axios.get(`${API}/promoted-posts`).catch(() => ({ data: [] })),
        axios.get(`${API}/feed/bars`).catch(() => ({ data: [] }))
      ]);
      
      setBars(barsRes.data);
      setFeaturedBars(featuredRes.data);
      setPromotedPosts(promoRes.data || []);
      setBarFeed(feedRes.data || []);
      
      // Fetch stats for each bar
      const statsPromises = barsRes.data.map(bar => 
        axios.get(`${API}/bars/${bar.id}/stats`).then(res => ({ id: bar.id, stats: res.data }))
      );
      const statsResults = await Promise.all(statsPromises);
      const statsMap = {};
      statsResults.forEach(({ id, stats }) => {
        statsMap[id] = stats;
      });
      setBarStats(statsMap);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load bars');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ background: 'transparent' }}>
        <img 
          src="/nightbuzz-logo.png" 
          alt="NightBuZz" 
          className="h-40 mb-6 animate-pulse"
        />
        <div className="text-white text-xl">Loading bars...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative" style={{ background: 'transparent' }}>
      {patronMatrix && <MatrixRain />}
      <LocalMatrixToggle storageKey="nb-patron-mode" defaultMode="neon" />
      <VersionBadge variant="patron-home-v1" />
      {/* Header */}
      <header className="glass sticky top-0 z-[100] border-b border-slate-700">
        <div className="container mx-auto px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="/nightbuzz-logo.png" 
              alt="NightBuZz" 
              className="h-24 md:h-28 cursor-pointer"
              onClick={() => navigate('/home')}
              data-testid="app-logo"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/bar-owner/auth')}
              className="hidden md:flex border-indigo-500 text-indigo-400 hover:bg-indigo-500/10"
            >
              For Bar Owners
            </Button>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <GamificationStats compact />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/premium')}
              className="text-yellow-400 hover:bg-yellow-500/10 relative z-[101]"
              data-testid="premium-nav-btn"
            >
              <Crown className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Premium</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/feed')}
              className="text-white hover:bg-white/10 relative z-[101]"
              data-testid="feed-nav-btn"
            >
              <Rss className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Feed</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/leaderboard')}
              className="text-white hover:bg-white/10 relative z-[101]"
              data-testid="leaderboard-nav-btn"
            >
              <Trophy className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Ranks</span>
            </Button>
            <NotificationBell />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/reservations')}
              className="text-white hover:bg-white/10 relative z-[101]"
              data-testid="reservations-nav-btn"
            >
              <Calendar className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/bar-crawl')}
              className="text-amber-400 hover:bg-amber-500/10 relative z-[101]"
              data-testid="bar-crawl-nav-btn"
              title="Bar Crawl Planner"
            >
              <Route className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/matchmaking')}
              className="text-pink-400 hover:bg-pink-500/10 relative z-[101]"
              data-testid="matchmaking-nav-btn"
            >
              <Heart className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/singles-night')}
              className="text-red-400 hover:bg-red-500/10 relative z-[101]"
              data-testid="singles-night-nav-btn"
              title="Singles Night Events"
            >
              <Users className="w-4 h-4" />
              <Heart className="w-3 h-3 absolute -top-1 -right-1 text-pink-400" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/friends')}
              className="text-white hover:bg-white/10 relative z-[101]"
              data-testid="friends-nav-btn"
            >
              <UserPlus className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Friends</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/profile')}
              className="text-white hover:bg-white/10 relative z-[101] flex items-center gap-2"
              data-testid="profile-nav-btn"
            >
              {user?.profile_picture_url ? (
                <img 
                  src={user.profile_picture_url.startsWith('/api') ? `${API.replace('/api', '')}${user.profile_picture_url}` : user.profile_picture_url}
                  alt={user.username}
                  className="w-8 h-8 rounded-full object-cover border-2 border-purple-500"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-sm font-bold">
                  {user?.username?.charAt(0).toUpperCase() || 'U'}
                </div>
              )}
              <span className="hidden md:inline">{user?.username || 'Profile'}</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="text-white hover:bg-white/10 relative z-[101]"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
            {/* Dark Mode Toggle - Show/Hide Falling Drinks */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDarkMode(!darkMode)}
              className={`relative z-[101] text-xs px-3 ${
                darkMode 
                  ? 'bg-purple-600/20 border-purple-500 text-purple-300 hover:bg-purple-600/30' 
                  : 'bg-slate-600/20 border-slate-500 text-slate-300 hover:bg-slate-600/30'
              }`}
            >
              {darkMode ? '🍾 Drinks On' : '🍾 Drinks Off'}
            </Button>
            {/* Matrix Skin Toggle */}
            <Button
              variant="outline"
              size="sm"
              data-testid="matrix-skin-toggle"
              onClick={() => setMatrixSkin(!matrixSkin)}
              className={`relative z-[101] text-xs px-3 font-mono ${
                matrixSkin
                  ? 'bg-black border-green-500 text-green-400 hover:bg-green-900/30 shadow-[0_0_10px_rgba(0,255,65,0.6)]'
                  : 'bg-slate-600/20 border-slate-500 text-slate-300 hover:bg-slate-600/30'
              }`}
            >
              {matrixSkin ? '◉ Matrix' : '◎ Matrix'}
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="mb-8 fade-in">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>
            Discover the Hottest Spots
          </h2>
          <p className="text-base md:text-lg text-slate-300">
            Find busy bars, amazing drink specials, and tag your friends
          </p>
        </div>

        {/* Instagram-style Stories */}
        <StoriesBar user={user} />

        {/* Live time-limited bar specials */}
        <div className="mb-6">
          <LiveSpecialsFeed />
        </div>

        {/* Unified Bar Feed - Instagram Style */}
        <div className="mb-8" data-testid="bar-feed-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <img src="/nightbuzz-logo.png" alt="" className="h-40 sm:h-44" />
              <div>
                <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                  Buzzing Feed
                </h3>
                <p className="text-xs text-slate-400">What's hot right now</p>
              </div>
            </div>
            {/* View Toggle */}
            <div className="flex items-center gap-1 bg-slate-800 rounded-lg p-1" data-testid="feed-view-toggle">
              <button
                onClick={() => setFeedView('cards')}
                className={`px-2.5 py-1 rounded text-xs font-medium transition-all ${
                  feedView === 'cards' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'
                }`}
                data-testid="feed-view-cards"
              >
                <Map className="w-3.5 h-3.5 inline mr-1" />Images
              </button>
              <button
                onClick={() => setFeedView('list')}
                className={`px-2.5 py-1 rounded text-xs font-medium transition-all ${
                  feedView === 'list' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'
                }`}
                data-testid="feed-view-list"
              >
                <List className="w-3.5 h-3.5 inline mr-1" />List
              </button>
            </div>
          </div>

          {(() => {
            const top3 = barFeed.slice(0, 3);
            const rest = [...barFeed.slice(3)].sort((a, b) => a.name.localeCompare(b.name));
            const visibleRest = rest.slice(0, feedPage * FEED_PAGE_SIZE);
            const hasMore = visibleRest.length < rest.length;

            if (feedView === 'cards') {
              return (
                <>
                  {/* Top 3 - always shown */}
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
                    {top3.map((bar, idx) => (
                      <BarFeedCard key={bar.id} bar={bar} rank={idx + 1} stats={bar.stats} onCheckin={fetchBars} />
                    ))}
                  </div>
                  {/* Rest alphabetical */}
                  {visibleRest.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {visibleRest.map((bar) => (
                        <BarFeedCard key={bar.id} bar={bar} rank={0} stats={bar.stats} onCheckin={fetchBars} />
                      ))}
                    </div>
                  )}
                  {hasMore && (
                    <div className="flex justify-center mt-6">
                      <Button
                        data-testid="load-more-btn"
                        onClick={() => setFeedPage(p => p + 1)}
                        className="px-8 py-4 text-base font-bold bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white shadow-lg shadow-indigo-500/30 rounded-xl"
                      >
                        Load More ({rest.length - visibleRest.length} remaining)
                      </Button>
                    </div>
                  )}
                </>
              );
            }

            // LIST VIEW
            return (
              <div className="space-y-1">
                {/* Top 3 highlighted */}
                {top3.map((bar, idx) => {
                  const medals = ['', '\u{1F947}', '\u{1F948}', '\u{1F949}'];
                  return (
                    <div
                      key={bar.id}
                      onClick={() => navigate(`/bar/${bar.id}`)}
                      className="flex items-center gap-3 p-2.5 rounded-lg cursor-pointer border border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/10 transition-all"
                      data-testid={`feed-list-item-${bar.id}`}
                    >
                      <span className="text-xl w-7 text-center shrink-0">{medals[idx + 1]}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-white text-sm truncate">{bar.name}</p>
                        <p className="text-xs text-slate-400 truncate">{bar.address?.split(',')[0]}</p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0 text-xs">
                        {bar.rating && <span className="text-amber-400"><Star className="w-3 h-3 inline fill-amber-400" /> {bar.rating}</span>}
                        <span className="text-indigo-400"><Users className="w-3 h-3 inline" /> {bar.stats?.total_checkins || 0}</span>
                        <span className="text-slate-400">{bar.stats?.ratio || 'N/A'}</span>
                        <FollowVenueButton venueId={bar.id} compact />
                      </div>
                    </div>
                  );
                })}

                {/* Divider */}
                {rest.length > 0 && <div className="border-t border-slate-700/50 my-2" />}

                {/* Rest alphabetical */}
                {visibleRest.map((bar) => (
                  <div
                    key={bar.id}
                    onClick={() => navigate(`/bar/${bar.id}`)}
                    className="flex items-center gap-3 p-2.5 rounded-lg cursor-pointer border border-slate-700/30 hover:border-indigo-500/30 hover:bg-slate-800/50 transition-all"
                    data-testid={`feed-list-item-${bar.id}`}
                  >
                    <div className="w-7 text-center shrink-0 text-slate-600 text-xs font-mono">
                      {bar.name?.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white text-sm truncate">{bar.name}</p>
                      <p className="text-xs text-slate-500 truncate">{bar.address?.split(',')[0]}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 text-xs">
                      {bar.rating && <span className="text-amber-400"><Star className="w-3 h-3 inline fill-amber-400" /> {bar.rating}</span>}
                      <span className="text-indigo-400"><Users className="w-3 h-3 inline" /> {bar.stats?.total_checkins || 0}</span>
                      <span className="text-slate-400">{bar.stats?.ratio || 'N/A'}</span>
                      <FollowVenueButton venueId={bar.id} compact />
                    </div>
                  </div>
                ))}

                {hasMore && (
                  <div className="flex justify-center mt-6">
                    <Button
                      data-testid="load-more-btn"
                      onClick={() => setFeedPage(p => p + 1)}
                      className="px-8 py-4 text-base font-bold bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white shadow-lg shadow-indigo-500/30 rounded-xl"
                    >
                      Load More ({rest.length - visibleRest.length} remaining)
                    </Button>
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* Happy Hour Alerts & Tonight's Events & Bar Passport */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <HappyHourAlerts />
          <TonightEvents />
          <BarPassport />
        </div>

        <WeeklyChallenges />

        <div className="my-4 flex justify-center">
          <EnableNotificationsButton color="#00FF41" label="Enable Notifications" />
        </div>

        {/* Patron Instagram-style Social Feed */}
        <SocialFeed
          feedKey="patron"
          tokenKey="token"
          title="📸 Patron Social Feed"
          accentColor="#FF4444"
          glowColor="#CC0000"
          venueOptions={(allNearbyBars || []).map(b => ({ id: b.id, name: b.name }))}
          showLike={true}
        />

        {/* Map Section - Full Width Below Trending Bars */}
        <Card className="glass p-6 fade-in" data-testid="map-container">
          {/* Live Feed above map */}
          <LiveFeed />
          
          <div className="flex flex-col gap-4 mb-4">
            <h3 className="text-2xl font-semibold text-white flex items-center gap-4">
              <img src="/nightbuzz-logo.png" alt="" className="h-40" />
              Bar Locations
            </h3>
            
            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 bg-slate-800 rounded-lg p-1 w-fit">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setViewMode('map')}
                className={viewMode === 'map' 
                  ? 'bg-purple-600 text-white hover:bg-purple-700' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-700'}
              >
                <Map className="w-4 h-4 mr-1" />
                Map
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' 
                  ? 'bg-purple-600 text-white hover:bg-purple-700' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-700'}
              >
                <List className="w-4 h-4 mr-1" />
                List
              </Button>
            </div>
          </div>
          
          {/* Filter Buttons */}
          {googlePlacesEnabled && (
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Button
                size="sm"
                variant={barFilter === 'all' ? 'default' : 'outline'}
                onClick={() => setBarFilter('all')}
                className={barFilter === 'all' 
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' 
                  : 'border-slate-600 text-slate-300 hover:bg-slate-700'}
              >
                All Bars ({allNearbyBars.length})
              </Button>
              <Button
                size="sm"
                variant={barFilter === 'partners' ? 'default' : 'outline'}
                onClick={() => setBarFilter('partners')}
                className={barFilter === 'partners' 
                  ? 'bg-purple-600 text-white' 
                  : 'border-purple-500/50 text-purple-400 hover:bg-purple-500/20'}
              >
                <div className="w-3 h-3 rounded-full bg-purple-500 mr-2"></div>
                Partners ({allNearbyBars.filter(b => b.is_partner).length})
              </Button>
              <Button
                size="sm"
                variant={barFilter === 'non-partners' ? 'default' : 'outline'}
                onClick={() => setBarFilter('non-partners')}
                className={barFilter === 'non-partners' 
                  ? 'bg-gray-600 text-white' 
                  : 'border-gray-500/50 text-gray-400 hover:bg-gray-500/20'}
              >
                <div className="w-3 h-3 rounded-full bg-gray-500 mr-2"></div>
                Non-Partners ({allNearbyBars.filter(b => !b.is_partner).length})
              </Button>
            </div>
          )}
          
          {/* MAP VIEW */}
          {viewMode === 'map' && (
          <div className="h-[500px] rounded-lg overflow-hidden">
            <MapContainer
              center={userLocation ? [userLocation.lat, userLocation.lng] : [40.7589, -73.9851]}
              zoom={14}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
              <MarkerClusterGroup
                key={barFilter}
                chunkedLoading
                iconCreateFunction={(cluster) => {
                  const count = cluster.getChildCount();
                  const hasPartner = cluster.getAllChildMarkers().some(m => m.options.isPartner);
                  const size = count < 10 ? 40 : count < 20 ? 50 : 60;
                  
                  return L.divIcon({
                    html: `<div style="
                      background: ${hasPartner ? 'linear-gradient(135deg, #9333EA, #EC4899)' : '#6B7280'};
                      width: ${size}px;
                      height: ${size}px;
                      border-radius: 50%;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      color: white;
                      font-weight: bold;
                      font-size: ${size / 3}px;
                      border: 3px solid white;
                      box-shadow: 0 4px 15px rgba(147, 51, 234, 0.5);
                    ">${count}</div>`,
                    className: 'custom-cluster-icon',
                    iconSize: L.point(size, size, true),
                  });
                }}
                maxClusterRadius={60}
                spiderfyOnMaxZoom={true}
                showCoverageOnHover={false}
              >
                {googlePlacesEnabled && filteredBars.map(bar => (
                  <Marker 
                    key={bar.id} 
                    position={[bar.latitude, bar.longitude]}
                    icon={bar.is_partner ? partnerIcon : nonPartnerIcon}
                    isPartner={bar.is_partner}
                  >
                    <Popup>
                      <div className="p-2 min-w-[200px]">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-bold text-lg">{bar.name}</h4>
                          {bar.is_partner && (
                            <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full">Partner</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 mb-2">{bar.address}</p>
                        {bar.is_partner ? (
                          <>
                            {barStats[bar.id] && (
                              <div className="mb-2">
                                <div className="text-sm">
                                  <span className="font-semibold">Status:</span>{' '}
                                  {barStats[bar.id].is_busy ? (
                                    <span className="text-green-500">🔥 Busy!</span>
                                  ) : (
                                    <span className="text-slate-400">Quiet</span>
                                  )}
                                </div>
                                <div className="text-sm">
                                  <span className="font-semibold">Ratio:</span> {barStats[bar.id].girl_guy_ratio}
                                </div>
                              </div>
                            )}
                            <Button
                              size="sm"
                              onClick={() => navigate(`/bar/${bar.id}`)}
                              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                            >
                              View Details
                            </Button>
                          </>
                        ) : (
                          <div className="text-center py-2">
                            <p className="text-xs text-gray-500 mb-2">Real bar from Google</p>
                            {bar.rating > 0 && (
                              <p className="text-sm mb-2">⭐ {bar.rating} rating</p>
                            )}
                            {bar.open_now !== undefined && (
                              <p className={`text-xs mb-2 ${bar.open_now ? 'text-green-500' : 'text-red-400'}`}>
                                {bar.open_now ? '✅ Open Now' : '❌ Closed'}
                              </p>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                const placeId = bar.google_place_id || bar.id;
                                window.location.href = `/bar/google/${placeId}`;
                              }}
                              className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md text-sm font-medium cursor-pointer"
                            >
                              View Bar Details
                            </button>
                          </div>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                ))}
                {!googlePlacesEnabled && bars.map(bar => (
                  <Marker key={bar.id} position={[bar.latitude, bar.longitude]} icon={partnerIcon} isPartner={true}>
                    <Popup>
                      <div className="p-2">
                        <h4 className="font-bold text-lg mb-1">{bar.name}</h4>
                        <p className="text-sm text-slate-300 mb-2">{bar.address}</p>
                        {barStats[bar.id] && (
                          <div className="mb-2">
                            <div className="text-sm">
                              <span className="font-semibold">Status:</span>{' '}
                              {barStats[bar.id].is_busy ? (
                                <span className="text-green-400">🔥 Busy!</span>
                              ) : (
                                <span className="text-slate-400">Quiet</span>
                              )}
                            </div>
                            <div className="text-sm">
                              <span className="font-semibold">Ratio:</span> {barStats[bar.id].girl_guy_ratio}
                            </div>
                          </div>
                        )}
                        <Button
                          size="sm"
                          onClick={() => navigate(`/bar/${bar.id}`)}
                          className="w-full bg-purple-600 hover:bg-purple-700"
                        >
                          View Details
                        </Button>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MarkerClusterGroup>
            </MapContainer>
          </div>
          )}
          
          {/* LIST VIEW */}
          {viewMode === 'list' && (
            <div className="grid md:grid-cols-2 gap-4">
              {/* Partner Bars List */}
              <div className="bg-slate-800/50 rounded-lg p-4 border border-purple-500/30">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"></div>
                  <h4 className="font-semibold text-white">Partner Bars</h4>
                  <span className="text-xs text-purple-400">({filteredBars.filter(b => b.is_partner).length})</span>
                </div>
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {filteredBars.filter(b => b.is_partner).length > 0 ? (
                    filteredBars.filter(b => b.is_partner).map(bar => (
                      <div 
                        key={bar.id}
                        onClick={() => navigate(`/bar/${bar.id}`)}
                        className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg cursor-pointer hover:bg-purple-500/20 transition-all border border-slate-700 hover:border-purple-500/50"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-lg">
                            {bar.name?.charAt(0)}
                          </div>
                          <div>
                            <p className="font-medium text-white">{bar.name}</p>
                            <p className="text-sm text-slate-400">{bar.address}</p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {barStats[bar.id]?.is_busy && (
                            <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full">🔥 Busy</span>
                          )}
                          {barStats[bar.id]?.girl_guy_ratio && (
                            <span className="text-xs text-slate-400">{barStats[bar.id].girl_guy_ratio}</span>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-8">No partner bars {barFilter !== 'all' ? 'matching filter' : 'nearby'}</p>
                  )}
                </div>
              </div>
              
              {/* Non-Partner Bars List */}
              <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-600/30">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 rounded-full bg-gray-500"></div>
                  <h4 className="font-semibold text-white">Other Nearby Bars</h4>
                  <span className="text-xs text-slate-400">({filteredBars.filter(b => !b.is_partner).length})</span>
                </div>
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {filteredBars.filter(b => !b.is_partner).length > 0 ? (
                    filteredBars.filter(b => !b.is_partner).map(bar => (
                      <div 
                        key={bar.id}
                        onClick={() => {
                          const placeId = bar.google_place_id || bar.id;
                          navigate(`/bar/google/${placeId}`);
                        }}
                        className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg cursor-pointer hover:bg-slate-700/50 transition-all border border-slate-700 hover:border-slate-500"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-lg bg-slate-700 flex items-center justify-center text-slate-300 font-bold text-lg">
                            {bar.name?.charAt(0)}
                          </div>
                          <div>
                            <p className="font-medium text-white">{bar.name}</p>
                            <p className="text-sm text-slate-400">{bar.address}</p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {bar.rating > 0 && (
                            <span className="text-sm text-yellow-400">⭐ {bar.rating}</span>
                          )}
                          {bar.open_now !== undefined && (
                            <span className={`text-xs ${bar.open_now ? 'text-green-400' : 'text-red-400'}`}>
                              {bar.open_now ? '● Open' : '● Closed'}
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-8">No other bars {barFilter !== 'all' ? 'matching filter' : 'found'}</p>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Bar Lists below map (only in map view) */}
          {viewMode === 'map' && googlePlacesEnabled && allNearbyBars.length > 0 && (
            <div className="mt-6 grid md:grid-cols-2 gap-4">
              {/* Partner Bars List */}
              <div className="bg-slate-800/50 rounded-lg p-4 border border-purple-500/30">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"></div>
                  <h4 className="font-semibold text-white">Partner Bars</h4>
                  <span className="text-xs text-purple-400">({allNearbyBars.filter(b => b.is_partner).length})</span>
                </div>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {allNearbyBars.filter(b => b.is_partner).length > 0 ? (
                    allNearbyBars.filter(b => b.is_partner).map(bar => (
                      <div 
                        key={bar.id}
                        onClick={() => navigate(`/bar/${bar.id}`)}
                        className="flex items-center justify-between p-2 bg-slate-900/50 rounded-lg cursor-pointer hover:bg-purple-500/20 transition-all border border-slate-700 hover:border-purple-500/50"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                            {bar.name?.charAt(0)}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-white">{bar.name}</p>
                            <p className="text-xs text-slate-400 truncate max-w-[150px]">{bar.address}</p>
                          </div>
                        </div>
                        {barStats[bar.id]?.is_busy && (
                          <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full">🔥 Busy</span>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-4">No partner bars nearby</p>
                  )}
                </div>
              </div>
              
              {/* Non-Partner Bars List */}
              <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-600/30">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 rounded-full bg-gray-500"></div>
                  <h4 className="font-semibold text-white">Other Nearby Bars</h4>
                  <span className="text-xs text-slate-400">({allNearbyBars.filter(b => !b.is_partner).length})</span>
                </div>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {allNearbyBars.filter(b => !b.is_partner).length > 0 ? (
                    allNearbyBars.filter(b => !b.is_partner).map(bar => (
                      <div 
                        key={bar.id}
                        onClick={() => {
                          const placeId = bar.google_place_id || bar.id;
                          navigate(`/bar/google/${placeId}`);
                        }}
                        className="flex items-center justify-between p-2 bg-slate-900/50 rounded-lg cursor-pointer hover:bg-slate-700/50 transition-all border border-slate-700 hover:border-slate-500"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center text-slate-300 font-bold">
                            {bar.name?.charAt(0)}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-white">{bar.name}</p>
                            <p className="text-xs text-slate-400 truncate max-w-[150px]">{bar.address}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {bar.rating > 0 && (
                            <span className="text-xs text-yellow-400">⭐ {bar.rating}</span>
                          )}
                          {bar.open_now !== undefined && (
                            <span className={`text-xs ${bar.open_now ? 'text-green-400' : 'text-red-400'}`}>
                              {bar.open_now ? '● Open' : '● Closed'}
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-4">No other bars found</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
      
      {/* Footer with Logo */}
      <footer className="glass border-t border-slate-700 mt-12 py-6">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img 
                src="/nightbuzz-logo.png" 
                alt="NightBuZz" 
                className="h-36"
              />
              <div>
                <p className="text-white font-semibold">NightBuZz</p>
                <p className="text-xs text-slate-400">Find the hottest bars near you</p>
                <a href="mailto:support@getnightbuzz.com" className="text-xs text-purple-400 hover:text-purple-300">support@getnightbuzz.com</a>
              </div>
            </div>
            <div className="flex flex-col items-center md:items-end gap-2">
              <div className="flex items-center gap-4 text-sm">
                <a href="/terms" className="text-slate-400 hover:text-white">Terms & Conditions</a>
                <span className="text-slate-600">|</span>
                <a href="/privacy" className="text-slate-400 hover:text-white">Privacy Policy</a>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <img src="/nightbuzz-logo.png" alt="" className="h-12 opacity-40" />
                <span>© 2026 NightBuZz</span>
                <span className="hidden md:inline">•</span>
                <span className="hidden md:inline">Discover • Connect • Party</span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;